import AccountBoxIcon from "@mui/icons-material/AccountBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  Grid,
  Modal,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getUrl } from "aws-amplify/storage";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import noImage from "../../images/noImage.png";
import { fetchTeamData, fetchEventTeamData } from "../../api/teamData";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../context/GlobalProvider";
import { getSpecificTeam } from "../../api/teams";

async function GetSpecificTeam(teamNumber) {
  const data = await getSpecificTeam(teamNumber);
  return data;
}

function GetSignedUrl({ team_number }) {
  const [signed, setSigned] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const getUrlResult = await getUrl({
        path: ({ identityId }) => `public/${team_number}/${team_number}_0.jpg`,
      });
      setSigned(getUrlResult.url);
    };
    fetchData();
  }, []);

  return (
    <>
      <img
        height={"100%"}
        width={"100%"}
        src={signed || noImage}
        style={{
          borderRadius: 5,
        }}
        onError={() => setSigned(null)}
        onClick={() => {
          signed !== null ? setOpen(true) : setOpen(false);
        }}
      />
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "70%", md: "60%", lg: "40%" },
            bgcolor: "background.light",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            aspectRatio: 1,
            textAlign: "center",
          }}
        >
          <img
            height={"100%"}
            width={"100%"}
            src={signed}
            style={{
              borderRadius: 5,
            }}
          />
          <Button
            variant="contained"
            onClick={() => setOpen(false)}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
}

export function TeamTile({ team = {}, event = "" }) {
  const { setTargetAnalysisPage, addPage } = useGlobal();
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState(null);
  const [statsShown, setStatsShown] = useState(false);
  const [teamDataGotten, setTeamDataGotten] = useState(false);

  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);

  const [hasIconPicture, setHasIconPicture] = useState(
    team?.icon_picture !== null
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      setCardHeight(entry.contentRect.height);
    });

    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const fetchTeamData = async () => {
      const result = await GetSpecificTeam(team.team_number);
      try {
        const parsedData = JSON.parse(result?.body);
        setHasIconPicture(
          Array.isArray(parsedData?.data)
            ? parsedData?.data[0]?.icon_picture
            : null
        );
      } catch (error) {
        console.error("Error parsing team data:", error);
        setHasIconPicture(null);
      }
    };

    fetchTeamData();
  }, [team]);

  const handleClick = () => {
    if (event === "") {
      addPage(`Team-${team.team_number}`);
      setTargetAnalysisPage("stats");
      navigate(`/analysis/team/${team.team_number}`);
    } else {
      addPage(`TeamEvent-${team.team_number}-${event}`);
      setTargetAnalysisPage("stats");
      navigate(`/analysis/event/${event}/team/${team.team_number}`);
    }
  };

  const abortControllerRef = useRef(null);

  const handleHover = (type) => {
    if (type === "enter") {
      setStatsShown(true);

      if (event === "" && teamData === null) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        setTeamDataGotten(true);
        fetchTeamData(Number(team.team_number), 2025, signal)
          .then((data) => {
            setTeamData(data);
            if (data !== null) {
              console.log(data);
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Fetch error:", error);
            }
          });
      } else if (event !== "" && teamData === null) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        setTeamDataGotten(true);
        fetchEventTeamData(Number(team.team_number), event, signal)
          .then((data) => {
            setTeamData(data);
            if (data !== null) {
              console.log(data);
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Fetch error:", error);
            }
          });
      }
    } else {
      setStatsShown(false);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  };

  return (
    <Grid item xs={12} lg={6} xl={4} sx={{ p: 1 }}>
      <Card
        ref={cardRef}
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          borderRadius: 5,
          backgroundColor: "background.light",
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{ width: "100%", height: "100%" }}
        >
          <Stack sx={{ width: "50%" }}>
            <Typography
              sx={{
                height: "22%",
                fontSize: cardHeight * 0.12,
                display: "flex",
                ml: 2,
              }}
            >
              <b>{team.team_number}</b>{" "}
              {event !== "" && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: cardHeight * 0.06,
                    ml: 0.5,
                    mb: 1,
                    alignContent: "center",
                  }}
                >
                  ({event.replace(/^\d+/, "").toUpperCase()})
                </Typography>
              )}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ width: "100%", height: "11%", pl: 2, alignItems: "center" }}
            >
              {hasIconPicture && (
                <img
                  src={"data:image/png;base64, " + hasIconPicture}
                  alt={team.team_number + " Icon"}
                  style={{
                    width: cardHeight * 0.1,
                    height: cardHeight * 0.1,
                    margin: 2,
                    borderColor: "accent.main",
                    borderStyle: "solid",
                    borderWidth: cardHeight * 0.006,
                    borderRadius: 5,
                  }}
                />
              )}
              {!hasIconPicture && (
                <AccountBoxIcon sx={{ fontSize: cardHeight * 0.08 }} />
              )}
              <Typography
                sx={{
                  width: "75%",
                  maxHeight: cardHeight * 0.14,
                  fontSize: cardHeight * 0.05,
                  overflow: "auto",
                  scrollbarWidth: "none",
                }}
              >
                <b>{team.team_name ?? team.nickname}</b>
              </Typography>
            </Stack>
            <Box
              sx={{
                width: (cardHeight / 3) * 2,
                height: (cardHeight / 3) * 2,
                padding: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GetSignedUrl team_number={team?.team_number} />
            </Box>
          </Stack>
          <Stack
            onClick={() => handleClick()}
            onMouseEnter={() => handleHover("enter")}
            onMouseLeave={() => handleHover("leave")}
            sx={{ width: "50%", cursor: "pointer" }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 5,
                padding: 2,
                backgroundColor: "background.light",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "#222",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!statsShown && (
                  <Stack
                    sx={{
                      width: "90%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.05,
                        overflow: "auto",
                        scrollbarWidth: "none",
                      }}
                    >
                      Hover to See Stats
                    </Typography>
                    <VisibilityIcon sx={{ fontSize: cardHeight * 0.15 }} />
                  </Stack>
                )}
                {statsShown && teamData !== null && (
                  <Stack
                    sx={{
                      width: "95%",
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.04,
                        overflow: "auto",
                        scrollbarWidth: "none",
                        p: 1,
                      }}
                    >
                      Click To Go To Team Page
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        p: 1,
                      }}
                    >
                      {event === "" && (
                        <DataGrid
                          rows={[
                            {
                              id: 1,
                              type: "EPA",
                              data: `${
                                Math.round(
                                  teamData.epa.total_points.mean * 100
                                ) / 100
                              }`,
                            },
                            {
                              id: 2,
                              type: "W / L",
                              data: `${teamData.record.wins}-${teamData.record.losses}-${teamData.record.ties}`,
                            },
                            {
                              id: 3,
                              type: "Next Event",
                              data: `${teamData.competing.next_event_key}`,
                            },
                            {
                              id: 4,
                              type: "World Rank",
                              data: `${teamData.epa.ranks.total.rank} / ${teamData.epa.ranks.total.team_count}`,
                            },
                            {
                              id: 5,
                              type: "Percentile",
                              data: `${
                                Math.round(
                                  teamData.epa.ranks.total.percentile * 1000
                                ) / 10
                              }%`,
                            },
                            {
                              id: 6,
                              type: "District Rank",
                              data: `${teamData.epa.ranks.district.rank} / ${teamData.epa.ranks.district.team_count}`,
                            },
                          ]}
                          columns={[
                            {
                              field: "type",
                              headerName: "Type",
                              flex: 1,
                              editable: false,
                              sortable: false,
                              disableColumnMenu: true,
                              headerAlign: "center",
                              align: "center",
                            },
                            {
                              field: "data",
                              headerName: "Data",
                              flex: 1,
                              editable: false,
                              sortable: false,
                              disableColumnMenu: true,
                              headerAlign: "center",
                              align: "center",
                            },
                          ]}
                          pagination={false}
                          hideFooter={true}
                          checkboxSelection={false}
                          disableRowSelectionOnClick={true}
                          disableColumnMenu
                          disableColumnSorting={true}
                          sx={{
                            "& .MuiDataGrid-cell": {
                              fontSize: cardHeight * 0.037,
                              cursor: "default",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                              fontSize: cardHeight * 0.04,
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: "bold",
                            },
                            "& .MuiDataGrid-root": { border: "none" },
                            "& .MuiDataGrid-columnHeader": {
                              pointerEvents: "none",
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                              { outline: "none" },
                          }}
                          headerHeight={cardHeight * 0.08}
                          rowHeight={cardHeight * 0.08}
                        />
                      )}
                      {event !== "" && (
                        <DataGrid
                          rows={[
                            {
                              id: 1,
                              type: "W / L",
                              data: `${teamData.record.total.wins}-${teamData.record.total.losses}-${teamData.record.total.ties}`,
                            },
                          ]}
                          columns={[
                            {
                              field: "type",
                              headerName: "Type",
                              flex: 1,
                              editable: false,
                              sortable: false,
                              disableColumnMenu: true,
                              headerAlign: "center",
                              align: "center",
                            },
                            {
                              field: "data",
                              headerName: "Data",
                              flex: 1,
                              editable: false,
                              sortable: false,
                              disableColumnMenu: true,
                              headerAlign: "center",
                              align: "center",
                            },
                          ]}
                          pagination={false}
                          hideFooter={true}
                          checkboxSelection={false}
                          disableRowSelectionOnClick={true}
                          disableColumnMenu
                          disableColumnSorting={true}
                          sx={{
                            "& .MuiDataGrid-cell": {
                              fontSize: cardHeight * 0.037,
                              cursor: "default",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                              fontSize: cardHeight * 0.04,
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: "bold",
                            },
                            "& .MuiDataGrid-root": { border: "none" },
                            "& .MuiDataGrid-columnHeader": {
                              pointerEvents: "none",
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                              { outline: "none" },
                          }}
                          headerHeight={cardHeight * 0.08}
                          rowHeight={cardHeight * 0.08}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.03,
                        overflow: "auto",
                        scrollbarWidth: "none",
                        p: 1,
                      }}
                    ></Typography>
                  </Stack>
                )}
                {statsShown && teamData === null && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                    flexDirection="column"
                  >
                    <CircularProgress />
                    <Typography
                      variant="h6"
                      sx={{
                        marginTop: 2,
                        textAlign: "center",
                        fontSize: cardHeight * 0.05,
                      }}
                    >
                      Loading Data
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Stack>
        </Stack>
      </Card>
    </Grid>
  );
}

export function EventTile({ team = "", event = "" }) {
  const { setTargetAnalysisPage, addPage } = useGlobal();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);
  const [statsShown, setStatsShown] = useState(false);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      setCardHeight(entry.contentRect.height);
    });

    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleClick = () => {
    if (team === "") {
      addPage(`Event-${event.eventCode}`);
      setTargetAnalysisPage("overview");
      navigate(`/analysis/event/${event.value}`);
    } else {
      addPage(`TeamEvent-${team}-${event.value}`);
      setTargetAnalysisPage("stats");
      navigate(`/analysis/event/${event.value}/team/${team}`);
    }
  };

  return (
    <Grid item xs={12} lg={6} xl={4} sx={{ p: 1 }}>
      <Card
        ref={cardRef}
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          borderRadius: 5,
          backgroundColor: "background.light",
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{ width: "100%", height: "100%" }}
        >
          <Stack spacing={2} sx={{ width: "50%", p: 2, pl: 3 }}>
            <Typography
              sx={{
                width: "95%",
                height: "20%",
                fontSize: cardHeight * 0.06,
                overflow: "auto",
                scrollbarWidth: "none",
              }}
            >
              <b>{event.name}</b>
            </Typography>
            <Typography
              sx={{
                width: "95%",
                maxHeight: cardHeight * 0.15,
                fontSize: cardHeight * 0.05,
                overflow: "auto",
                scrollbarWidth: "none",
              }}
            >
              Week: <b>{event.week}</b>
            </Typography>
            <Typography
              sx={{
                width: "95%",
                maxHeight: cardHeight * 0.15,
                fontSize: cardHeight * 0.05,
                overflow: "auto",
                scrollbarWidth: "none",
              }}
            >
              {"Dates: "}
              <br />
              <b>
                {new Date(event.startDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </b>{" "}
              {" to "}
              <b>
                {new Date(event.endDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </b>{" "}
            </Typography>
            <Typography
              sx={{
                width: "95%",
                maxHeight: cardHeight * 0.15,
                fontSize: cardHeight * 0.05,
                overflow: "auto",
                scrollbarWidth: "none",
              }}
            >
              Event Code: <b>{event.eventCode}</b>
            </Typography>
            <Typography
              sx={{
                width: "95%",
                maxHeight: cardHeight * 0.15,
                fontSize: cardHeight * 0.05,
                overflow: "auto",
                scrollbarWidth: "none",
                cursor: event.location !== "Unknown" ? "pointer" : "default",
              }}
              onClick={() => {
                if (event.location !== "Unknown") {
                  window.open(event.gmapsURL);
                }
              }}
            >
              Location:{" "}
              {event.location === "Unknown" ? (
                "Unknown"
              ) : (
                <u>{event.location}</u>
              )}
            </Typography>
          </Stack>
          <Stack
            onClick={() => handleClick()}
            onMouseEnter={() => setStatsShown(true)}
            onMouseLeave={() => setStatsShown(false)}
            sx={{ width: "50%", cursor: "pointer" }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 5,
                padding: 2,

                backgroundColor: "background.light",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "background.light",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!statsShown && (
                  <Stack
                    sx={{
                      width: "90%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.05,
                        overflow: "auto",
                        scrollbarWidth: "none",
                      }}
                    >
                      Hover to See Stats
                    </Typography>
                    <VisibilityIcon sx={{ fontSize: cardHeight * 0.15 }} />
                  </Stack>
                )}
                {statsShown && (
                  <Stack
                    sx={{
                      width: "95%",
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.04,
                        overflow: "auto",
                        scrollbarWidth: "none",
                        p: 1,
                      }}
                    >
                      Click To Go To Event Page
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: cardHeight * 0.05,
                          overflow: "auto",
                          scrollbarWidth: "none",
                        }}
                      >
                        W.I.P.
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: cardHeight * 0.03,
                        overflow: "auto",
                        scrollbarWidth: "none",
                        p: 1,
                      }}
                    ></Typography>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Stack>
        </Stack>
      </Card>
    </Grid>
  );
}
